import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import type { DashboardOptions, LocationGroup, VehicleCategoryOption } from '@/entities/dashboard';
import {
  FilterSelect,
  DateRangePicker,
  SelectOption,
  IndeterminateCheckbox,
  type DateRangeValue,
  type FilterGroup
} from '@/shared/ui';

import {
  getMetricOptions,
  getGroupByOptions,
  getStatusOptions,
  getTransportGroupOptions,
  getBreakdownOptions,
  getTransportModeOptions,
  getConsignmentsStatusOptions
} from '../config/filter-options';
import { LocationsFilter } from './locations-filter';
import { OrganizationsFilter } from './organizations-filter';
import { ConsignmentNumberFilter } from './consignment-number-filter';
import { OrganizationSelectFilter } from './organization-select-filter';
import { VehicleCategoryFilter } from './vehicle-category-filter';
import { TextFilter } from './text-filter';

interface DashboardFilterBarProps {
  options: DashboardOptions;
  onOptionChange: (key: keyof DashboardOptions, value: string) => void;
  variant?: 'storage' | 'transport' | 'service' | 'service-users' | 'mapping' | 'consignments';

  // For storage variant with locations
  locationGroups?: LocationGroup[];
  selectedLocations?: string[];
  onLocationsChange?: (locations: string[]) => void;
  locationsLoading?: boolean;

  // For organizations filter
  organizationGroups?: FilterGroup[];
  selectedOrganizations?: string[];
  onOrganizationsChange?: (organizations: string[]) => void;
  organizationsLoading?: boolean;

  // For transport variant with vehicle categories
  vehicleCategories?: VehicleCategoryOption[];

  // For date range
  onDateRangeChange?: (value: DateRangeValue) => void;
  showDateRange?: boolean;

  // For component remount on project/site change
  resetKey?: string;

  // For mapping variant
  consignmentNumber?: string;
  onConsignmentNumberChange?: (value: string) => void;
  organizationOptions?: SelectOption[];
  selectedOrganization?: string;
  onOrganizationChange?: (value: string | undefined) => void;
  onOrganizationSearchChange?: (value: string) => void;
  isLoadingOrganizations?: boolean;
  vehicleCategoryOptions?: SelectOption[];
  selectedVehicleCategories?: string[];
  onVehicleCategoriesChange?: (values: string[]) => void;
  showVehicleCategories?: boolean;

  // For consignments variant
  waybill?: string;
  onWaybillChange?: (value: string) => void;
  allSites?: boolean;
  onAllSitesChange?: (checked: boolean) => void;
}

export function DashboardFilterBar({
  options,
  onOptionChange,
  variant = 'storage',
  locationGroups,
  selectedLocations,
  onLocationsChange,
  locationsLoading,
  organizationGroups,
  selectedOrganizations,
  onOrganizationsChange,
  organizationsLoading,
  onDateRangeChange,
  showDateRange = false,
  resetKey,
  vehicleCategories,
  // Mapping variant props
  consignmentNumber,
  onConsignmentNumberChange,
  organizationOptions,
  selectedOrganization,
  onOrganizationChange,
  onOrganizationSearchChange,
  isLoadingOrganizations,
  vehicleCategoryOptions,
  selectedVehicleCategories,
  onVehicleCategoriesChange,
  showVehicleCategories,
  // Consignments variant props
  waybill,
  onWaybillChange,
  allSites,
  onAllSitesChange
}: DashboardFilterBarProps) {
  const { t } = useTranslation();

  switch (variant) {
    case 'consignments':
      return (
        <div className="flex gap-4 flex-wrap items-end">
          {onConsignmentNumberChange && (
            <ConsignmentNumberFilter value={consignmentNumber || ''} onChange={onConsignmentNumberChange} />
          )}

          {onDateRangeChange && (
            <DateRangePicker
              key={resetKey}
              label={t('cmrlist.filters.createdFrom.label', 'Created from')}
              defaultFromDate={options.fromDate}
              defaultToDate={options.toDate}
              onChange={onDateRangeChange}
            />
          )}

          <FilterSelect
            label={t('cmrlist.filters.status.label', 'Status')}
            value={options.status || ''}
            onChange={(value) => onOptionChange('status', value)}
            options={getConsignmentsStatusOptions(t)}
          />

          {organizationOptions && onOrganizationChange && (
            <OrganizationSelectFilter
              options={organizationOptions}
              value={selectedOrganization}
              onChange={onOrganizationChange}
              onSearchChange={onOrganizationSearchChange}
              isLoading={isLoadingOrganizations}
            />
          )}

          {onWaybillChange && (
            <TextFilter
              label={t('cmrlist.filters.waybill.label', 'Waybill')}
              value={waybill || ''}
              onChange={onWaybillChange}
              placeholder={t('cmrlist.filters.waybill.placeholder', 'Waybill')}
            />
          )}

          {onAllSitesChange && (
            <div className="flex items-center h-10">
              <IndeterminateCheckbox
                checked={allSites}
                onCheckedChange={(checked: boolean | 'indeterminate') => onAllSitesChange(checked === true)}
                label={t('cmrlist.filters.allSites.label', 'All sites')}
                id="all-sites-filter"
              />
            </div>
          )}
        </div>
      );
    case 'mapping':
      return (
        <div className="flex gap-4 mb-6 flex-wrap">
          {onConsignmentNumberChange && (
            <ConsignmentNumberFilter value={consignmentNumber || ''} onChange={onConsignmentNumberChange} />
          )}

          {organizationOptions && onOrganizationChange && (
            <OrganizationSelectFilter
              options={organizationOptions}
              value={selectedOrganization}
              onChange={onOrganizationChange}
              onSearchChange={onOrganizationSearchChange}
              isLoading={isLoadingOrganizations}
            />
          )}

          {showVehicleCategories && vehicleCategoryOptions && onVehicleCategoriesChange && (
            <VehicleCategoryFilter
              options={vehicleCategoryOptions}
              value={selectedVehicleCategories || []}
              onChange={onVehicleCategoriesChange}
            />
          )}
        </div>
      );
    case 'transport':
      return (
        <TransportFilters
          options={options}
          onOptionChange={onOptionChange}
          t={t}
          onDateRangeChange={onDateRangeChange}
          resetKey={resetKey}
          vehicleCategories={vehicleCategories}
        />
      );
    case 'service':
    case 'service-users':
      return (
        <ServiceUsersFilters
          options={options}
          onOptionChange={onOptionChange}
          t={t}
          locationGroups={locationGroups}
          selectedLocations={selectedLocations}
          onLocationsChange={onLocationsChange}
          locationsLoading={locationsLoading}
          organizationGroups={organizationGroups}
          selectedOrganizations={selectedOrganizations}
          onOrganizationsChange={onOrganizationsChange}
          organizationsLoading={organizationsLoading}
          onDateRangeChange={onDateRangeChange}
          resetKey={resetKey}
        />
      );
    default:
      return (
        <StorageFilters
          options={options}
          onOptionChange={onOptionChange}
          t={t}
          locationGroups={locationGroups}
          selectedLocations={selectedLocations}
          onLocationsChange={onLocationsChange}
          locationsLoading={locationsLoading}
          onDateRangeChange={onDateRangeChange}
          showDateRange={showDateRange}
          resetKey={resetKey}
        />
      );
  }
}

interface FilterProps {
  options: DashboardOptions;
  onOptionChange: (key: keyof DashboardOptions, value: string) => void;
  t: TFunction<'translation', undefined>;
}

interface StorageFilterProps extends FilterProps {
  locationGroups?: LocationGroup[];
  selectedLocations?: string[];
  onLocationsChange?: (locations: string[]) => void;
  locationsLoading?: boolean;
  onDateRangeChange?: (value: DateRangeValue) => void;
  showDateRange?: boolean;
  resetKey?: string;
}

interface ServiceUsersFilterProps extends FilterProps {
  locationGroups?: LocationGroup[];
  selectedLocations?: string[];
  onLocationsChange?: (locations: string[]) => void;
  locationsLoading?: boolean;
  organizationGroups?: FilterGroup[];
  selectedOrganizations?: string[];
  onOrganizationsChange?: (organizations: string[]) => void;
  organizationsLoading?: boolean;
  onDateRangeChange?: (value: DateRangeValue) => void;
  resetKey?: string;
}

function StorageFilters({
  options,
  onOptionChange,
  t,
  locationGroups,
  selectedLocations,
  onLocationsChange,
  locationsLoading,
  onDateRangeChange,
  showDateRange,
  resetKey
}: StorageFilterProps) {
  return (
    <div className="flex gap-4 mb-6 flex-wrap">
      {locationGroups && selectedLocations && onLocationsChange && (
        <LocationsFilter
          locationGroups={locationGroups}
          selectedLocations={selectedLocations}
          onLocationsChange={onLocationsChange}
          isLoading={locationsLoading}
        />
      )}
      <FilterSelect
        label={t('welcome.metric.label', 'Metric')}
        value={options.metric || 'W'}
        onChange={(value) => onOptionChange('metric', value)}
        options={getMetricOptions(t)}
      />
      <FilterSelect
        label={t('welcome.groupby.label', 'Group by')}
        value={options.group || 'D'}
        onChange={(value) => onOptionChange('group', value)}
        options={getGroupByOptions(t)}
        width="lg"
      />
      {showDateRange && onDateRangeChange && (
        <DateRangePicker
          key={resetKey}
          label={t('welcome.daterange.label')}
          defaultPreset={options.period || 'L6M'}
          defaultFromDate={options.fromDate}
          defaultToDate={options.toDate}
          onChange={onDateRangeChange}
        />
      )}
    </div>
  );
}

interface TransportFilterProps extends FilterProps {
  onDateRangeChange?: (value: DateRangeValue) => void;
  resetKey?: string;
  vehicleCategories?: VehicleCategoryOption[];
}

function TransportFilters({
  options,
  onOptionChange,
  t,
  onDateRangeChange,
  resetKey,
  vehicleCategories
}: TransportFilterProps) {
  return (
    <div className="flex gap-4 mb-6 flex-wrap">
      <FilterSelect
        label={t('welcome.metric.label', 'Metric')}
        value={options.metric || 'W'}
        onChange={(value) => onOptionChange('metric', value)}
        options={getMetricOptions(t)}
      />
      <FilterSelect
        label={t('welcome.groupby.label', 'Group by')}
        value={options.group || 'D'}
        onChange={(value) => onOptionChange('group', value)}
        options={getTransportGroupOptions(t)}
        width="lg"
      />
      <FilterSelect
        label={t('filter.breakdown.label', 'Breakdown')}
        value={options.breakDown || 'NO'}
        onChange={(value) => onOptionChange('breakDown', value)}
        options={getBreakdownOptions(t)}
      />
      {vehicleCategories && (
        <FilterSelect
          label={t('transportMode.description.label', 'Transport Mode')}
          value={options.vehicleCategoryId?.toString() || 'NO'}
          onChange={(value) => onOptionChange('vehicleCategoryId', value)}
          options={getTransportModeOptions(t, vehicleCategories)}
        />
      )}
      {onDateRangeChange && (
        <DateRangePicker
          key={resetKey}
          label={t('welcome.daterange.label', 'Date Range')}
          defaultPreset={options.period || 'L6M'}
          defaultFromDate={options.fromDate}
          defaultToDate={options.toDate}
          onChange={onDateRangeChange}
        />
      )}
      <FilterSelect
        label={t('request.status.label', 'Status')}
        value={options.status || 'DI'}
        onChange={(value) => onOptionChange('status', value)}
        options={getStatusOptions(t)}
      />
    </div>
  );
}

function ServiceUsersFilters({
  options,
  onOptionChange,
  t,
  locationGroups,
  selectedLocations,
  onLocationsChange,
  locationsLoading,
  organizationGroups,
  selectedOrganizations,
  onOrganizationsChange,
  organizationsLoading,
  onDateRangeChange,
  resetKey
}: ServiceUsersFilterProps) {
  return (
    <div className="flex gap-4 mb-6 flex-wrap">
      {locationGroups && selectedLocations && onLocationsChange && (
        <LocationsFilter
          locationGroups={locationGroups}
          selectedLocations={selectedLocations}
          onLocationsChange={onLocationsChange}
          isLoading={locationsLoading}
        />
      )}
      {organizationGroups && selectedOrganizations && onOrganizationsChange && (
        <OrganizationsFilter
          organizationGroups={organizationGroups}
          selectedOrganizations={selectedOrganizations}
          onOrganizationsChange={onOrganizationsChange}
          isLoading={organizationsLoading}
        />
      )}
      <FilterSelect
        label={t('welcome.metric.label', 'Metric')}
        value={options.metric || 'W'}
        onChange={(value) => onOptionChange('metric', value)}
        options={getMetricOptions(t)}
      />
      {onDateRangeChange && (
        <DateRangePicker
          key={resetKey}
          label={t('welcome.daterange.label')}
          defaultPreset={options.period || 'L6M'}
          defaultFromDate={options.fromDate}
          defaultToDate={options.toDate}
          onChange={onDateRangeChange}
        />
      )}
    </div>
  );
}
